import React, {useState, useEffect} from "react";
import {Button} from "../ui/button";
import Icon from "../Icon";
import {t} from "i18next";
import {WidgetContent} from "../widget/Widget";
import {WidgetSettings} from "src/interfaces/settings";
import {
  Assistant,
  AssistantChatFeedback,
  AssistantChatMessage,
  ChatAssistantMetadata,
  Thread,
} from "src/interfaces/assistant";
import {getLocalStorageItem, setLocalStorageItem} from "src/utilities/cookie";
import {PostAssistantFeedback} from "src/services/backend";
import moment from "moment";
import {CURRENT_TIME_FORMAT} from "src/config/constants";
import {AlertError} from "src/reducers/store";

interface FeedbackSectionProps {
  knowledgeBaseId: number;
  assistant: Assistant;
  messages: AssistantChatMessage[];
  widgetSettings: WidgetSettings;
  threadId: string;
  metaData: ChatAssistantMetadata;
  navigateTo: (content: WidgetContent) => void;
}

const FeedbackSection = ({
  navigateTo,
  messages,
  widgetSettings,
  threadId,
  metaData,
  knowledgeBaseId,
  assistant,
}: FeedbackSectionProps) => {
  const [isFeedbackGiven, setIsFeedbackGiven] = useState(false);

  const handleFeedback = (feedback: "positive" | "negative") => {
    const startTime = moment(messages[0].createdAt).format(CURRENT_TIME_FORMAT);
    const endTime = moment(messages[messages.length - 1].createdAt).format(CURRENT_TIME_FORMAT);

    const feedbackData: AssistantChatFeedback = {
      positive: feedback === "positive",
      startTime,
      endTime,
      transcript: messages.map((msg) => ({
        text: msg.text,
        sender: msg.sender,
        timestamp: msg.createdAt.toString(),
      })),
    };
    const existingThread = JSON.parse(getLocalStorageItem(`assistantThread-chat-${knowledgeBaseId}`, "{}", true));
    setLocalStorageItem(
      `assistantThread-chat-${knowledgeBaseId}`,
      JSON.stringify({
        ...existingThread,
        threadId,
        isFeedbackGiven: true,
      }),
    );
    postAssistantFeedback(knowledgeBaseId, assistant.id, threadId, feedbackData);

    setIsFeedbackGiven(true);
  };

  const postAssistantFeedback = async (
    knowledgeBaseId: number,
    assistantId: string,
    threadId: string,
    feedback: AssistantChatFeedback,
  ) => {
    try {
      await PostAssistantFeedback(knowledgeBaseId, assistantId, threadId, feedback);
    } catch (error) {
      AlertError("Error posting feedback");
    }
  };

  useEffect(() => {
    const storedThread = getLocalStorageItem<Thread | null>(`assistantThread-chat-${knowledgeBaseId}`, null, true);
    if (storedThread && storedThread.threadId === threadId && storedThread.isFeedbackGiven) {
      setIsFeedbackGiven(true);
    }
  }, [threadId, knowledgeBaseId]);

  return (
    <div className="pt-3 px-4 pb-5 flex flex-col justify-between border-t">
      <div>
        {!isFeedbackGiven ? (
          <div className="flex flex-col">
            <span>{t("WAS_THIS_HELPFUL")}</span>
            <div className="flex gap-2 mt-1">
              <Button
                variant="outline"
                onClick={() => handleFeedback("positive")}
                className="flex gap-2"
                style={{
                  backgroundColor: assistant.metadata?.userMessageColor || "#E1E7EA",
                }}
              >
                <Icon name="thumbs-up" className="mb-1" /> {t("YES")}
              </Button>
              <Button
                variant="outline"
                onClick={() => handleFeedback("negative")}
                className="flex gap-2"
                style={{
                  backgroundColor: assistant.metadata?.userMessageColor || "#E1E7EA",
                }}
              >
                <Icon name="thumbs-down" className="mt-1" /> {t("NO")}
              </Button>
            </div>
          </div>
        ) : (
          <p>{t("THANKS_FOR_FEEDBACK")}</p>
        )}
      </div>
      {widgetSettings.formEnabled && (
        <div className="flex flex-col mt-2">
          <p>{t("YOU_CAN_ALSO_USE_OUR")}</p>
          <Button
            variant="outline"
            className="text-lg"
            style={{
              backgroundColor: metaData?.assistantMessageColor,
              color: metaData?.assistantMessageTextColor,
            }}
            onClick={() => navigateTo(WidgetContent.FORM_GROUP_SELECT)}
          >
            {t("FORMS")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FeedbackSection;
